/**
 * Staff Slice for Wadakaroo
 *
 * Manages staff state including:
 * - Staff CRUD operations
 * - Available roles and profiles
 * - Document management
 * - Leave plan management
 * - Pagination state
 * - Modal states for create/edit
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getStaffListApi,
    getStaffApi,
    createStaffApi,
    updateStaffApi,
    deleteStaffApi,
    resendWelcomeEmailApi,
    getStaffDocumentsApi,
    uploadStaffDocumentApi,
    deleteStaffDocumentApi,
    getStaffLeavePlansApi,
    createStaffLeavePlanApi,
    updateStaffLeavePlanApi,
    deleteStaffLeavePlanApi,
    getStaffLeavePlansInRangeApi,
    getAllStaffLeavePlansInRangeApi,
    getStaffRolesApi,
    getDocumentTypesApi,
    getAssignableProfilesApi,
    getUiViewProfilesApi,
} from '../../services/staffApi';

// ============================================
// Async Thunks
// ============================================

/**
 * Fetch paginated staff list
 */
export const fetchStaffList = createAsyncThunk(
    'staff/fetchStaffList',
    async ({ page = 0, size = 20, activeOnly = false, search = '' } = {}, { rejectWithValue }) => {
        try {
            const data = await getStaffListApi({ page, size, activeOnly, search });
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch staff list';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch a single staff member by UUID
 */
export const fetchStaff = createAsyncThunk(
    'staff/fetchStaff',
    async (uuid, { rejectWithValue }) => {
        try {
            const data = await getStaffApi(uuid);
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch staff member';
            return rejectWithValue(message);
        }
    }
);

/**
 * Create a new staff member
 */
export const createStaff = createAsyncThunk(
    'staff/createStaff',
    async (staffData, { rejectWithValue }) => {
        try {
            const data = await createStaffApi(staffData);
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to create staff member';
            return rejectWithValue(message);
        }
    }
);

/**
 * Update an existing staff member
 */
export const updateStaff = createAsyncThunk(
    'staff/updateStaff',
    async ({ uuid, staffData }, { rejectWithValue }) => {
        try {
            const data = await updateStaffApi(uuid, staffData);
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to update staff member';
            return rejectWithValue(message);
        }
    }
);

/**
 * Delete (deactivate) a staff member
 */
export const deleteStaff = createAsyncThunk(
    'staff/deleteStaff',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteStaffApi(uuid);
            return uuid;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to delete staff member';
            return rejectWithValue(message);
        }
    }
);

/**
 * Resend welcome email
 */
export const resendWelcomeEmail = createAsyncThunk(
    'staff/resendWelcomeEmail',
    async (uuid, { rejectWithValue }) => {
        try {
            const data = await resendWelcomeEmailApi(uuid);
            return { uuid, ...data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to send welcome email';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch staff documents
 */
export const fetchStaffDocuments = createAsyncThunk(
    'staff/fetchStaffDocuments',
    async (staffUuid, { rejectWithValue }) => {
        try {
            const data = await getStaffDocumentsApi(staffUuid);
            return { staffUuid, documents: data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch documents';
            return rejectWithValue(message);
        }
    }
);

/**
 * Upload a document
 */
export const uploadDocument = createAsyncThunk(
    'staff/uploadDocument',
    async ({ staffUuid, file, documentType, documentName, notes }, { rejectWithValue }) => {
        try {
            const data = await uploadStaffDocumentApi(staffUuid, file, documentType, documentName, notes);
            return { staffUuid, document: data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to upload document';
            return rejectWithValue(message);
        }
    }
);

/**
 * Delete a document
 */
export const deleteDocument = createAsyncThunk(
    'staff/deleteDocument',
    async ({ staffUuid, documentUuid }, { rejectWithValue }) => {
        try {
            await deleteStaffDocumentApi(staffUuid, documentUuid);
            return { staffUuid, documentUuid };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to delete document';
            return rejectWithValue(message);
        }
    }
);

// ============================================
// Leave Plan Thunks
// ============================================

/**
 * Fetch leave plans for a staff member
 */
export const fetchStaffLeavePlans = createAsyncThunk(
    'staff/fetchStaffLeavePlans',
    async (staffUuid, { rejectWithValue }) => {
        try {
            const data = await getStaffLeavePlansApi(staffUuid);
            return { staffUuid, leavePlans: data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch leave plans';
            return rejectWithValue(message);
        }
    }
);

/**
 * Create a leave plan
 */
export const createStaffLeavePlan = createAsyncThunk(
    'staff/createStaffLeavePlan',
    async ({ staffUuid, startDate, endDate }, { rejectWithValue }) => {
        try {
            const data = await createStaffLeavePlanApi(staffUuid, { startDate, endDate });
            return { staffUuid, leavePlan: data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to create leave plan';
            return rejectWithValue(message);
        }
    }
);

/**
 * Update a leave plan
 */
export const updateStaffLeavePlan = createAsyncThunk(
    'staff/updateStaffLeavePlan',
    async ({ staffUuid, leavePlanUuid, startDate, endDate }, { rejectWithValue }) => {
        try {
            const data = await updateStaffLeavePlanApi(staffUuid, leavePlanUuid, { startDate, endDate });
            return { staffUuid, leavePlan: data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to update leave plan';
            return rejectWithValue(message);
        }
    }
);

/**
 * Delete a leave plan
 */
export const deleteStaffLeavePlan = createAsyncThunk(
    'staff/deleteStaffLeavePlan',
    async ({ staffUuid, leavePlanUuid }, { rejectWithValue }) => {
        try {
            await deleteStaffLeavePlanApi(staffUuid, leavePlanUuid);
            return { staffUuid, leavePlanUuid };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to delete leave plan';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch leave plans for ONE staff member that overlap a given window.
 * Mostly a convenience wrapper; most callers should use fetchAllLeavePlansInRange.
 */
export const fetchStaffLeavePlansInRange = createAsyncThunk(
    'staff/fetchStaffLeavePlansInRange',
    async ({ staffUuid, from, to }, { rejectWithValue }) => {
        try {
            const data = await getStaffLeavePlansInRangeApi(staffUuid, from, to);
            return { staffUuid, leavePlans: data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch leave plans';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch ALL leave plans across the org that overlap a given window.
 * Reducer groups them into `leavePlansByStaff[staffUuid] = [...]`
 * for easy lookup from the planner and assign modal.
 */
export const fetchAllLeavePlansInRange = createAsyncThunk(
    'staff/fetchAllLeavePlansInRange',
    async ({ from, to }, { rejectWithValue }) => {
        try {
            const data = await getAllStaffLeavePlansInRangeApi(from, to);
            return { from, to, leavePlans: data };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch leave plans in range';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch available staff roles
 */
export const fetchStaffRoles = createAsyncThunk(
    'staff/fetchStaffRoles',
    async (_, { rejectWithValue }) => {
        try {
            const data = await getStaffRolesApi();
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch staff roles';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch available document types
 */
export const fetchDocumentTypes = createAsyncThunk(
    'staff/fetchDocumentTypes',
    async (_, { rejectWithValue }) => {
        try {
            const data = await getDocumentTypesApi();
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch document types';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch assignable profiles
 */
export const fetchAssignableProfiles = createAsyncThunk(
    'staff/fetchAssignableProfiles',
    async (_, { rejectWithValue }) => {
        try {
            const data = await getAssignableProfilesApi();
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch profiles';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch UI View Profiles
 */
export const fetchUiViewProfiles = createAsyncThunk(
    'staff/fetchUiViewProfiles',
    async (_, { rejectWithValue }) => {
        try {
            const data = await getUiViewProfilesApi();
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch UI view profiles';
            return rejectWithValue(message);
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    // Staff list
    staffList: [],
    isLoading: false,
    error: null,

    // Pagination
    pagination: {
        page: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
    },

    // Filters
    filters: {
        activeOnly: false,
        search: '',
    },

    // Selected staff (for edit modal)
    selectedStaff: null,
    isLoadingStaff: false,

    // Available options
    roles: [],
    documentTypes: [],
    assignableProfiles: [],
    uiViewProfiles: [],
    isLoadingOptions: false,

    // Modal states
    isCreateModalOpen: false,
    isEditModalOpen: false,
    isDeleteModalOpen: false,
    isDocumentModalOpen: false,
    isLeavePlanModalOpen: false,
    staffToDelete: null,

    // Leave plan state
    leavePlans: [], // Leave plans for the currently-open staff member
    isLoadingLeavePlans: false,
    isSavingLeavePlan: false,
    isDeletingLeavePlan: false,

    // Org-wide leave plan lookup, keyed by staff uuid. Populated by
    // fetchAllLeavePlansInRange. Used by the planner to overlay blocked
    // time slots and by the assign modal to filter out unavailable staff.
    leavePlansByStaff: {},           // { [staffUuid]: [{ uuid, startDate, endDate, staffUuid }] }
    leavePlansRange: null,           // { from, to } — the window currently cached
    isLoadingLeavePlansRange: false,

    // Operation states
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isUploadingDocument: false,
    isRefreshing: false, // Shows spinner overlay on table after create/update/delete
    operationError: null,
    operationSuccess: null,
};

const staffSlice = createSlice({
    name: 'staff',
    initialState,
    reducers: {
        // Modal controls
        openCreateModal: (state) => {
            state.isCreateModalOpen = true;
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeCreateModal: (state) => {
            state.isCreateModalOpen = false;
            state.operationError = null;
        },
        openEditModal: (state, action) => {
            state.isEditModalOpen = true;
            // Only replace selectedStaff if a payload is provided.
            // Otherwise keep whatever fetchStaff.fulfilled set (the full DTO).
            if (action.payload) {
                state.selectedStaff = action.payload;
            }
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeEditModal: (state) => {
            state.isEditModalOpen = false;
            state.selectedStaff = null;
            state.operationError = null;
        },
        openDeleteModal: (state, action) => {
            state.isDeleteModalOpen = true;
            state.staffToDelete = action.payload;
            state.operationError = null;
        },
        closeDeleteModal: (state) => {
            state.isDeleteModalOpen = false;
            state.staffToDelete = null;
            state.operationError = null;
        },
        openDocumentModal: (state, action) => {
            state.isDocumentModalOpen = true;
            state.selectedStaff = action.payload;
            state.operationError = null;
        },
        closeDocumentModal: (state) => {
            state.isDocumentModalOpen = false;
            state.operationError = null;
        },
        openLeavePlanModal: (state, action) => {
            state.isLeavePlanModalOpen = true;
            state.selectedStaff = action.payload;
            state.leavePlans = [];
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeLeavePlanModal: (state) => {
            state.isLeavePlanModalOpen = false;
            state.selectedStaff = null;
            state.leavePlans = [];
            state.operationError = null;
        },

        // Filter controls
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
            state.pagination.page = 0; // Reset to first page when filters change
        },
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        setPageSize: (state, action) => {
            state.pagination.size = action.payload;
            state.pagination.page = 0; // Reset to first page when size changes
        },

        // Clear states
        clearError: (state) => {
            state.error = null;
            state.operationError = null;
        },
        clearOperationSuccess: (state) => {
            state.operationSuccess = null;
        },
        clearSelectedStaff: (state) => {
            state.selectedStaff = null;
        },
    },
    extraReducers: (builder) => {
        // ---- Fetch Staff List ----
        builder.addCase(fetchStaffList.pending, (state) => {
            // Only show full loading state if not refreshing after an operation
            if (!state.isRefreshing) {
                state.isLoading = true;
            }
            state.error = null;
        });
        builder.addCase(fetchStaffList.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isRefreshing = false; // Clear refreshing spinner
            // Backend omits `content` entirely when totalElements is 0, so
            // default to an empty array instead of letting it become undefined.
            state.staffList = action.payload.content || [];
            state.pagination = {
                page: action.payload.page,
                size: action.payload.size,
                totalElements: action.payload.totalElements,
                totalPages: action.payload.totalPages,
                hasNext: action.payload.hasNext,
                hasPrevious: action.payload.hasPrevious,
            };
        });
        builder.addCase(fetchStaffList.rejected, (state, action) => {
            state.isLoading = false;
            state.isRefreshing = false; // Clear refreshing spinner
            state.error = action.payload || 'Failed to fetch staff list';
        });

        // ---- Fetch Single Staff ----
        builder.addCase(fetchStaff.pending, (state) => {
            state.isLoadingStaff = true;
        });
        builder.addCase(fetchStaff.fulfilled, (state, action) => {
            state.isLoadingStaff = false;
            state.selectedStaff = action.payload;
        });
        builder.addCase(fetchStaff.rejected, (state, action) => {
            state.isLoadingStaff = false;
            state.operationError = action.payload;
        });

        // ---- Create Staff ----
        builder.addCase(createStaff.pending, (state) => {
            state.isCreating = true;
            state.operationError = null;
        });
        builder.addCase(createStaff.fulfilled, (state, action) => {
            state.isCreating = false;
            // NOTE: do NOT auto-close the create modal here. The team-member
            // wizard is a multi-step flow and needs to remain open after the
            // staff record is created so the user can continue with Step 2
            // (UI views) and Step 3 (permissions). The wizard calls onClose
            // explicitly when the user dismisses it.
            state.isRefreshing = true; // Show spinner while table refreshes
            state.operationSuccess = action.payload.message || 'Staff member created successfully';
        });
        builder.addCase(createStaff.rejected, (state, action) => {
            state.isCreating = false;
            state.operationError = action.payload;
        });

        // ---- Update Staff ----
        builder.addCase(updateStaff.pending, (state) => {
            state.isUpdating = true;
            state.operationError = null;
        });
        builder.addCase(updateStaff.fulfilled, (state, action) => {
            state.isUpdating = false;
            // NOTE: do NOT auto-close the edit modal or null selectedStaff
            // here. The team-member wizard is multi-step and needs to stay
            // open while the user works through Step 2 and Step 3. The wizard
            // calls onClose explicitly (which dispatches closeEditModal) when
            // the user dismisses it.
            state.operationSuccess = `Staff member "${action.payload.fullName}" updated successfully`;
            // Update in list
            const index = state.staffList.findIndex(s => s.uuid === action.payload.uuid);
            if (index !== -1) {
                state.staffList[index] = {
                    ...state.staffList[index],
                    firstName: action.payload.firstName,
                    lastName: action.payload.lastName,
                    fullName: action.payload.fullName,
                    email: action.payload.email,
                    mobile: action.payload.mobile,
                    role: action.payload.role,
                    profileName: action.payload.profileName,
                    uiViewProfileName: action.payload.uiViewProfileName,
                    uiViewProfileUuid: action.payload.uiViewProfileUuid,
                    active: action.payload.active,
                };
            }
        });
        builder.addCase(updateStaff.rejected, (state, action) => {
            state.isUpdating = false;
            state.operationError = action.payload;
        });

        // ---- Delete Staff ----
        builder.addCase(deleteStaff.pending, (state) => {
            state.isDeleting = true;
            state.operationError = null;
        });
        builder.addCase(deleteStaff.fulfilled, (state, action) => {
            state.isDeleting = false;
            state.isDeleteModalOpen = false;
            state.staffToDelete = null;
            state.operationSuccess = 'Staff member deactivated successfully';
            // Update in list (mark as inactive)
            const index = state.staffList.findIndex(s => s.uuid === action.payload);
            if (index !== -1) {
                state.staffList[index].active = false;
            }
        });
        builder.addCase(deleteStaff.rejected, (state, action) => {
            state.isDeleting = false;
            state.operationError = action.payload;
        });

        // ---- Resend Welcome Email ----
        builder.addCase(resendWelcomeEmail.pending, (state) => {
            state.isUpdating = true;
            state.operationError = null;
        });
        builder.addCase(resendWelcomeEmail.fulfilled, (state, action) => {
            state.isUpdating = false;
            state.operationSuccess = 'Welcome email sent successfully';
        });
        builder.addCase(resendWelcomeEmail.rejected, (state, action) => {
            state.isUpdating = false;
            state.operationError = action.payload;
        });

        // ---- Fetch Documents ----
        builder.addCase(fetchStaffDocuments.fulfilled, (state, action) => {
            if (state.selectedStaff && state.selectedStaff.uuid === action.payload.staffUuid) {
                state.selectedStaff.documents = action.payload.documents;
            }
        });

        // ---- Upload Document ----
        builder.addCase(uploadDocument.pending, (state) => {
            state.isUploadingDocument = true;
            state.operationError = null;
        });
        builder.addCase(uploadDocument.fulfilled, (state, action) => {
            state.isUploadingDocument = false;
            state.operationSuccess = 'Document uploaded successfully';
            if (state.selectedStaff && state.selectedStaff.uuid === action.payload.staffUuid) {
                if (!state.selectedStaff.documents) {
                    state.selectedStaff.documents = [];
                }
                state.selectedStaff.documents.unshift(action.payload.document);
            }
        });
        builder.addCase(uploadDocument.rejected, (state, action) => {
            state.isUploadingDocument = false;
            state.operationError = action.payload;
        });

        // ---- Delete Document ----
        builder.addCase(deleteDocument.fulfilled, (state, action) => {
            state.operationSuccess = 'Document deleted successfully';
            if (state.selectedStaff && state.selectedStaff.uuid === action.payload.staffUuid) {
                state.selectedStaff.documents = state.selectedStaff.documents.filter(
                    d => d.uuid !== action.payload.documentUuid
                );
            }
        });

        // ---- Fetch Leave Plans ----
        builder.addCase(fetchStaffLeavePlans.pending, (state) => {
            state.isLoadingLeavePlans = true;
            state.operationError = null;
        });
        builder.addCase(fetchStaffLeavePlans.fulfilled, (state, action) => {
            state.isLoadingLeavePlans = false;
            if (state.selectedStaff && state.selectedStaff.uuid === action.payload.staffUuid) {
                state.leavePlans = action.payload.leavePlans || [];
            }
        });
        builder.addCase(fetchStaffLeavePlans.rejected, (state, action) => {
            state.isLoadingLeavePlans = false;
            state.operationError = action.payload;
        });

        // ---- Create Leave Plan ----
        builder.addCase(createStaffLeavePlan.pending, (state) => {
            state.isSavingLeavePlan = true;
            state.operationError = null;
        });
        builder.addCase(createStaffLeavePlan.fulfilled, (state, action) => {
            state.isSavingLeavePlan = false;
            state.operationSuccess = 'Leave plan saved successfully';
            if (state.selectedStaff && state.selectedStaff.uuid === action.payload.staffUuid) {
                state.leavePlans.unshift(action.payload.leavePlan);
            }
        });
        builder.addCase(createStaffLeavePlan.rejected, (state, action) => {
            state.isSavingLeavePlan = false;
            state.operationError = action.payload;
        });

        // ---- Update Leave Plan ----
        builder.addCase(updateStaffLeavePlan.pending, (state) => {
            state.isSavingLeavePlan = true;
            state.operationError = null;
        });
        builder.addCase(updateStaffLeavePlan.fulfilled, (state, action) => {
            state.isSavingLeavePlan = false;
            state.operationSuccess = 'Leave plan updated successfully';
            if (state.selectedStaff && state.selectedStaff.uuid === action.payload.staffUuid) {
                const idx = state.leavePlans.findIndex(lp => lp.uuid === action.payload.leavePlan.uuid);
                if (idx !== -1) {
                    state.leavePlans[idx] = action.payload.leavePlan;
                }
            }
        });
        builder.addCase(updateStaffLeavePlan.rejected, (state, action) => {
            state.isSavingLeavePlan = false;
            state.operationError = action.payload;
        });

        // ---- Delete Leave Plan ----
        builder.addCase(deleteStaffLeavePlan.pending, (state) => {
            state.isDeletingLeavePlan = true;
            state.operationError = null;
        });
        builder.addCase(deleteStaffLeavePlan.fulfilled, (state, action) => {
            state.isDeletingLeavePlan = false;
            state.operationSuccess = 'Leave plan deleted successfully';
            if (state.selectedStaff && state.selectedStaff.uuid === action.payload.staffUuid) {
                state.leavePlans = state.leavePlans.filter(
                    lp => lp.uuid !== action.payload.leavePlanUuid
                );
            }
        });
        builder.addCase(deleteStaffLeavePlan.rejected, (state, action) => {
            state.isDeletingLeavePlan = false;
            state.operationError = action.payload;
        });

        // ---- Fetch Leave Plans In Range (single staff) ----
        builder.addCase(fetchStaffLeavePlansInRange.fulfilled, (state, action) => {
            state.leavePlansByStaff = {
                ...state.leavePlansByStaff,
                [action.payload.staffUuid]: action.payload.leavePlans || [],
            };
        });

        // ---- Fetch All Leave Plans In Range (org-wide) ----
        builder.addCase(fetchAllLeavePlansInRange.pending, (state) => {
            state.isLoadingLeavePlansRange = true;
        });
        builder.addCase(fetchAllLeavePlansInRange.fulfilled, (state, action) => {
            state.isLoadingLeavePlansRange = false;
            // Rebuild the map from scratch so stale staff disappear if they
            // no longer have leave in the current window.
            const byStaff = {};
            for (const lp of action.payload.leavePlans || []) {
                if (!lp.staffUuid) continue;
                if (!byStaff[lp.staffUuid]) byStaff[lp.staffUuid] = [];
                byStaff[lp.staffUuid].push(lp);
            }
            state.leavePlansByStaff = byStaff;
            state.leavePlansRange = { from: action.payload.from, to: action.payload.to };
        });
        builder.addCase(fetchAllLeavePlansInRange.rejected, (state, action) => {
            state.isLoadingLeavePlansRange = false;
            state.operationError = action.payload;
        });

        // ---- Fetch Options (Roles, Document Types, Profiles) ----
        builder.addCase(fetchStaffRoles.pending, (state) => {
            state.isLoadingOptions = true;
        });
        builder.addCase(fetchStaffRoles.fulfilled, (state, action) => {
            state.isLoadingOptions = false;
            state.roles = action.payload;
        });
        builder.addCase(fetchStaffRoles.rejected, (state, action) => {
            state.isLoadingOptions = false;
            state.operationError = action.payload;
        });

        builder.addCase(fetchDocumentTypes.fulfilled, (state, action) => {
            state.documentTypes = action.payload;
        });

        builder.addCase(fetchAssignableProfiles.pending, (state) => {
            state.isLoadingOptions = true;
        });
        builder.addCase(fetchAssignableProfiles.fulfilled, (state, action) => {
            state.isLoadingOptions = false;
            state.assignableProfiles = action.payload;
        });
        builder.addCase(fetchAssignableProfiles.rejected, (state, action) => {
            state.isLoadingOptions = false;
            state.operationError = action.payload;
        });

        // ---- Fetch UI View Profiles ----
        builder.addCase(fetchUiViewProfiles.pending, (state) => {
            state.isLoadingOptions = true;
        });
        builder.addCase(fetchUiViewProfiles.fulfilled, (state, action) => {
            state.isLoadingOptions = false;
            state.uiViewProfiles = action.payload;
        });
        builder.addCase(fetchUiViewProfiles.rejected, (state, action) => {
            state.isLoadingOptions = false;
            state.operationError = action.payload;
        });
    },
});

// ============================================
// Actions
// ============================================

export const {
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    openDocumentModal,
    closeDocumentModal,
    openLeavePlanModal,
    closeLeavePlanModal,
    setFilters,
    setPage,
    setPageSize,
    clearError,
    clearOperationSuccess,
    clearSelectedStaff,
} = staffSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectStaffList = (state) => state.staff.staffList;
export const selectIsLoading = (state) => state.staff.isLoading;
export const selectError = (state) => state.staff.error;
export const selectPagination = (state) => state.staff.pagination;
export const selectFilters = (state) => state.staff.filters;
export const selectSelectedStaff = (state) => state.staff.selectedStaff;
export const selectIsLoadingStaff = (state) => state.staff.isLoadingStaff;
export const selectRoles = (state) => state.staff.roles;
export const selectDocumentTypes = (state) => state.staff.documentTypes;
export const selectAssignableProfiles = (state) => state.staff.assignableProfiles;
export const selectUiViewProfiles = (state) => state.staff.uiViewProfiles;
export const selectIsLoadingOptions = (state) => state.staff.isLoadingOptions;
export const selectIsCreateModalOpen = (state) => state.staff.isCreateModalOpen;
export const selectIsEditModalOpen = (state) => state.staff.isEditModalOpen;
export const selectIsDeleteModalOpen = (state) => state.staff.isDeleteModalOpen;
export const selectIsDocumentModalOpen = (state) => state.staff.isDocumentModalOpen;
export const selectIsLeavePlanModalOpen = (state) => state.staff.isLeavePlanModalOpen;
export const selectStaffToDelete = (state) => state.staff.staffToDelete;
export const selectIsCreating = (state) => state.staff.isCreating;
export const selectIsUpdating = (state) => state.staff.isUpdating;
export const selectIsDeleting = (state) => state.staff.isDeleting;
export const selectIsUploadingDocument = (state) => state.staff.isUploadingDocument;
export const selectIsRefreshing = (state) => state.staff.isRefreshing;
export const selectOperationError = (state) => state.staff.operationError;
export const selectOperationSuccess = (state) => state.staff.operationSuccess;
export const selectLeavePlans = (state) => state.staff.leavePlans;
export const selectIsLoadingLeavePlans = (state) => state.staff.isLoadingLeavePlans;
export const selectIsSavingLeavePlan = (state) => state.staff.isSavingLeavePlan;
export const selectIsDeletingLeavePlan = (state) => state.staff.isDeletingLeavePlan;
export const selectLeavePlansByStaff = (state) => state.staff.leavePlansByStaff;
export const selectLeavePlansRange = (state) => state.staff.leavePlansRange;
export const selectIsLoadingLeavePlansRange = (state) => state.staff.isLoadingLeavePlansRange;

export default staffSlice.reducer;